import React, { useEffect, useImperativeHandle, useState, forwardRef } from 'react';
import { getCaptcha } from '../../api/getCaptcha';

// Anti-bot gate for the public web-request forms (Contact.js / Work.js) —
// reuses insurancex_mw's existing direct-login captcha. One-shot on the
// backend (consumed on every verify attempt, right or wrong — see
// authHelper.js), so the caller must call ref.refresh() after every submit
// attempt, success or failure, or a retry will always fail with a stale id.
const Captcha = forwardRef(function Captcha({ value, onChange, error, variant }, ref) {
  const [imageDataUri, setImageDataUri] = useState(null);
  const [captchaId, setCaptchaId] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await getCaptcha();
      setImageDataUri(data?.imageDataUri || null);
      setCaptchaId(data?.captchaId || null);
    } catch (err) {
      console.error('Error loading captcha:', err);
      setImageDataUri(null);
      setCaptchaId(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({ getCaptchaId: () => captchaId, refresh }));

  const input = (
    <input type="text" placeholder="Type the code" value={value} onChange={(e) => onChange(e.target.value)} autoComplete="off" />
  );

  const field = (
    <div className="captcha-field">
      <div className="captcha-field__code">
        {imageDataUri ? (
          <img className="captcha-field__image" src={imageDataUri} alt="captcha code" />
        ) : (
          <span className="captcha-field__placeholder">{loading ? 'Loading…' : 'Unavailable'}</span>
        )}
        <button type="button" className="captcha-field__refresh" onClick={refresh} disabled={loading} title="Get a new code" aria-label="Get a new code">
          ↻
        </button>
      </div>
      {input}
    </div>
  );

  // "plain" matches Work.js's contact-one form (simple text labels); the
  // default matches Contact.js's contact-two form (icon labels).
  if (variant === 'plain') {
    return (
      <>
        <div className="form-field-label">
          Enter Code Shown <span className="form-error">*</span>
        </div>
        {field}
        {error && <p className="form-error">{error}</p>}
      </>
    );
  }

  return (
    <div className="contact-two__input">
      <div className="contact-two__input__label">
        <i className="far fa-shield-check" />
        Enter Code Shown <span className="form-error">*</span>
      </div>
      {field}
      {error && <p className="form-error">{error}</p>}
    </div>
  );
});

export default Captcha;
